import { Typography, useTheme } from "@mui/material"
import { OreveButton } from "components/button/OreveButton.tsx"
import { StyledTextField } from "components/inputs/StyledTextField.tsx"
import { useForm, Controller } from "react-hook-form"
import { useMmsService } from "services/mms/context/MmsServiceContext.tsx"
import { useState, useEffect } from "react"
import WarningIcon from "assets/svg/warning-icon.svg"
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import {
    OrevePopup,
    OrevePopupContent,
    OrevePopupTitle,
} from "components/popup/OrevePopup.tsx"
import { useCardHolderContext } from "layouts/card-holder/context/useCardHolderContext.ts"

interface IDeactivateForm {
    reason: string
}

const getPrefix = (isVehicle: boolean) => (isVehicle ? "vehicle" : "driver")

export const DeactivateCardHolderPopup = () => {
    const [displayResponse, setDisplayResponse] = useState(false)

    const {
        openDeactivateCardHolder: open,
        onClosePopup: handleClose,
        driverSelected: driver,
    } = useCardHolderContext()

    const { cardHolderService } = useMmsService()
    const { useDeactivateCardHolder } = cardHolderService
    const {
        isLoading,
        success,
        sendRequest: deactivateDriver,
    } = useDeactivateCardHolder(driver?.id ?? "")

    const onDeactivate = async (data: IDeactivateForm) => {
        await deactivateDriver(data)
        setDisplayResponse(true)
    }

    useEffect(() => {
        if (open) setDisplayResponse(false)
    }, [open])

    if (!driver) return null

    const driverName =
        "name" in driver
            ? driver.name
            : "licensePlate" in driver
                ? driver.licensePlate
                : ""

    return (
        <OrevePopup open={open} handleClose={handleClose}>
            {displayResponse ? (
                <PopupFeedback
                    name={driverName}
                    success={success ?? false}
                    isLoading={isLoading}
                    handleClose={handleClose}
                />
            ) : (
                <PopupMain
                    name={driverName}
                    isLoading={isLoading}
                    onDeactivate={onDeactivate}
                />
            )}
        </OrevePopup>
    )
}

const PopupMain = ({
                       name,
                       isLoading,
                       onDeactivate,
                   }: {
    name: string
    isLoading: boolean
    onDeactivate: (data: IDeactivateForm) => void
}) => (
    <>
        <OrevePopupTitle>
            <DeactivatePopupTitle name={name} />
        </OrevePopupTitle>
        <OrevePopupContent>
            <DeactivateDriverForm
                onDeactivate={onDeactivate}
                isLoading={isLoading}
            />
        </OrevePopupContent>
    </>
)

const DeactivateDriverForm = ({
                                  onDeactivate,
                                  isLoading,
                              }: {
    onDeactivate: (data: IDeactivateForm) => void
    isLoading: boolean
}) => {
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: { reason: "" },
        mode: "onChange",
    })
    const theme = useTheme()
    const { isVehicle } = useCardHolderContext()
    const prefix = getPrefix(isVehicle)

    const onSubmitDeactivate = (data: IDeactivateForm) => onDeactivate(data)

    return (
        <form onSubmit={handleSubmit(onSubmitDeactivate)}>
            <Controller
                name="reason"
                control={control}
                rules={{ maxLength: 250 }}
                render={({ field }) => (
                    <StyledTextField
                        id="drivers-page-reason-deactivation"
                        placeholder="Vous pouvez saisir un motif de désactivation (250 caractères maximum)."
                        multiline
                        rows={2}
                        bgcolor={theme.palette.grey[100]}
                        error={!!errors.reason}
                        helperText={`${field.value.length}/250 caractères`}
                        {...field}
                        data-cy={`${prefix}__deactivate--a-reason`}
                    />
                )}
            />
            <OreveButton
                type="submit"
                value="Désactiver"
                variant="contained"
                color="primary"
                loading={isLoading}
                sx={{ mt: "1rem" }}
                data-cy={`${prefix}__deactivate--submit`}
            />
        </form>
    )
}

const DeactivatePopupTitle = ({ name }: { name: string }) => {
    const { isVehicle } = useCardHolderContext()
    const prefix = getPrefix(isVehicle)
    const titleText = isVehicle ? "véhicule" : "conducteur"

    return (
        <>
            <img src={WarningIcon} alt="warning icon" />
            <Typography
                variant="headlineSmall"
                component="div"
                data-cy={`${prefix}__deactivate--title`}
            >
                Désactiver {name}
            </Typography>
            <Typography
                variant="bodyLarge"
                sx={{ color: "grey.600", mt: "1rem" }}
                data-cy={`${prefix}__deactivate--subtitle`}
            >
                Pour quelle raison souhaitez-vous désactiver ce {titleText} ?
                <br />
                Toutes les cartes associées à ce {titleText} seront
                automatiquement désactivées.
            </Typography>
        </>
    )
}

const PopupFeedback = ({
                           name,
                           success,
                           isLoading,
                           handleClose,
                       }: {
    name: string
    success: boolean
    isLoading: boolean
    handleClose: () => void
}) => {
    const { isVehicle } = useCardHolderContext()
    const prefix = getPrefix(isVehicle)

    return (
        <>
            <OrevePopupTitle>
                {success ? (
                    <CheckCircleOutlinedIcon color="secondary" />
                ) : (
                    <ErrorOutlineIcon color="error" />
                )}
                <Typography
                    variant="headlineSmall"
                    component="div"
                    data-cy={`${prefix}__deactivate--${
                        success ? "success" : "error"
                    }`}
                >
                    {success
                        ? `${name} a été désactivé(e) avec succès`
                        : `${name} n'a pas pu être désactivé(e)`}
                </Typography>
            </OrevePopupTitle>
            <OrevePopupContent>
                <OreveButton
                    value="Retour à la liste"
                    variant="contained"
                    color="primary"
                    loading={isLoading}
                    onClick={handleClose}
                    data-cy={`${prefix}__deactivate--back-to-list`}
                />
            </OrevePopupContent>
        </>
    )
}
